import { DeviceClasses } from '@racemap/utilities/consts/events';
import {
  formatNestedObject,
  parseNestedObject,
  parseTagObjectLegacy,
} from '@racemap/utilities/formattings/formatting';
import {
  formatTimeDuration,
  timeDurationToMilliseconds,
} from '@racemap/utilities/formattings/times';
import { validateStarterAppId } from '@racemap/utilities/functions/validateStarterAppId';
import type { RacemapEvent, RacemapStarter } from '@racemap/utilities/types/events';
import type { User_Legacy } from '@racemap/utilities/types/users';
import { type Immutable, produce } from 'immer';
import Papa from 'papaparse';
import { getPlayerUrl } from 'src/lib/getPlayerUrl';
import { raceVideoLink } from 'src/lib/raceVideo';

type StarterCSV = Array<string>;
type ColumnMapping = Record<string, number>;
type NestedObjectsMapping = Record<string, Record<string, number>>;

const COLUMN_HEADERS = [
  'starter_id',
  'startnumber',
  'name',
  'imei',
  'device_type',
  'device_class',
  'device_id',
  'marker_color',
  'tags', // legacy, use the nested object collumns format instead
  'startTime',
  'endTime',
  'manualResult',
  'key',
];
const NESTED_OBJECT_HEADER_PREFIXES = ['times', 'tags'];

export function parseStartersCSVFile(file: File): Promise<Array<Partial<RacemapStarter>>> {
  return new Promise((resolve, reject) => {
    Papa.parse<StarterCSV>(file, {
      skipEmptyLines: true,
      error: reject,
      complete(results) {
        if (results.errors.length > 0) {
          reject(results.errors);
          return;
        }

        try {
          resolve(parseStarterRows(results.data));
        } catch (error) {
          reject(error);
        }
      },
    });
  });
}

function parseStarterRows(data: Array<StarterCSV>): Array<Partial<RacemapStarter>> {
  // Here we have an csv with at least one data-row and 2 data-columns
  // Format has to be:
  //  startnumber;name;imei;device_type;device_class;marker_color
  //  12;Neppi;4106012717;LK106;Tracker;#fff
  //  24;Peter;4106012718;LK106;Tracker;#fff
  if (data.length <= 1) throw new Error('Too few rows');
  if (data[0].length < 2) throw new Error('Too few columns');

  const headerRow = data[0].map((h) => h.trim());
  const columnMapping = mapColumns(headerRow);
  const nestedObjectsMapping = mapNestedObjectColumns(headerRow);

  for (const col of ['startnumber', 'name']) {
    if (columnMapping[col] == null) throw new Error(`Column ${col} missing.`);
  }

  if (columnMapping.tags != null && nestedObjectsMapping.tags != null) {
    throw new Error(
      'You cant use the old and the new format to store the tags in the csv. Remove the legacy format and try again.',
    );
  }

  return data.slice(1).map((row) => parseStarterRow(row, columnMapping, nestedObjectsMapping));
}

function mapColumns(headerRow: Array<string>): ColumnMapping {
  const columnMapping: ColumnMapping = {};
  for (const header of COLUMN_HEADERS) {
    const index = headerRow.indexOf(header);
    if (index < 0) continue;

    columnMapping[header] = index;
  }
  return columnMapping;
}

function mapNestedObjectColumns(headerRow: Array<string>): NestedObjectsMapping {
  const nestedObjectsMapping: NestedObjectsMapping = {};
  for (const headerPrefix of NESTED_OBJECT_HEADER_PREFIXES) {
    for (const csvHeader of headerRow) {
      if (!csvHeader.startsWith(`${headerPrefix}_`) && !csvHeader.startsWith(`${headerPrefix}.`))
        continue;

      nestedObjectsMapping[headerPrefix] ??= {};
      nestedObjectsMapping[headerPrefix][csvHeader] = headerRow.indexOf(csvHeader);
    }
  }
  return nestedObjectsMapping;
}

// this is for downwards compatibility, if someone used the old format
// while parsing the end time of a starter, we transform it to the new finish property
// of the nested times object
function replaceEndByFinish(timesNode?: Record<string, string>) {
  if (timesNode != null && timesNode.end != null && timesNode.finish == null) {
    timesNode.finish = timesNode.end;
    delete timesNode.end;
  }
  return timesNode;
}

function parseStarterRow(
  row: StarterCSV,
  columnMapping: ColumnMapping,
  nestedObjectsMapping: NestedObjectsMapping,
): Partial<RacemapStarter> {
  const manualResult = getValueOfRow(row, columnMapping.manualResult);

  return {
    id: getValueOfRow(row, columnMapping.starter_id),
    startNumber: getValueOfRow(row, columnMapping.startnumber),
    name: getValueOfRow(row, columnMapping.name),
    appId: getValueOfRow(row, columnMapping.imei),
    deviceType: getValueOfRow(row, columnMapping.device_type),
    deviceClass: (getValueOfRow(row, columnMapping.device_class) as DeviceClasses) || undefined,
    markerColor: getValueOfRow(row, columnMapping.marker_color),
    tags: {
      ...parseTagObjectLegacy(getValueOfRow(row, columnMapping.tags) || ''),
      ...parseNestedObject(row, nestedObjectsMapping.tags || {}),
    },
    startTime: getValueOfRow(row, columnMapping.startTime),
    endTime: getValueOfRow(row, columnMapping.endTime),
    manualFinishDuration:
      manualResult != null ? timeDurationToMilliseconds(manualResult) : undefined,
    key: getValueOfRow(row, columnMapping.key),
    deviceId: getValueOfRow(row, columnMapping.device_id) || null,
    times: replaceEndByFinish(parseNestedObject(row, nestedObjectsMapping.times || {})),
  };
}

function getValueOfRow(row: StarterCSV, index: number): string | undefined {
  const value = row[index];

  if (value == null) return undefined;
  return value.trim();
}

/** Per-starter links: the player for admins, the race video once the organiser turned videos on. */
function linkColumns(
  user: Immutable<User_Legacy>,
  event: Immutable<RacemapEvent>,
): Array<[string, (starter: RacemapStarter) => string]> {
  const columns: Array<[string, (starter: RacemapStarter) => string]> = [];
  if (user.admin) {
    columns.push(['player_link', (s) => getPlayerUrl(event, { starterIds: [s.id] })]);
  }
  if (event.playerOptions?.hasRaceVideo) {
    columns.push(['race_video_link', (s) => s.raceVideoUrl ?? raceVideoLink(event.id, s.id)]);
  }
  return columns;
}

export function buildStartersCSVFile(
  starters: Array<RacemapStarter>,
  user: Immutable<User_Legacy>,
  event: Immutable<RacemapEvent>,
) {
  let csvString =
    'startnumber;name;imei;device_type;device_class;marker_color;startTime;endTime;manualResult;key;starter_id;device_id;device_name';
  const possibleTags = Array.from(
    new Set(
      starters.reduce<Array<string>>((prev, s) => {
        return produce(prev, (draft) => {
          draft.push(...Array.from(Object.keys(s.tags)));
        });
      }, []),
    ),
  );
  if (possibleTags.length > 0) {
    csvString += `;tags.${possibleTags.join(';tags.')}`;
  }

  const possibleTimes = Array.from(
    new Set(
      starters.reduce<Array<string>>((prev, s) => {
        return produce(prev, (draft) => {
          draft.push(...Array.from(Object.keys(s.times)));
        });
      }, []),
    ),
  );
  if (possibleTimes.length > 0) {
    csvString += `;times.${possibleTimes.join(';times.')}`;
  }

  const links = linkColumns(user, event);
  for (const [header] of links) csvString += `;${header}`;

  for (const starter of starters) {
    let row = [
      starter.startNumber,
      starter.name,
      starter.appId,
      starter.deviceType,
      starter.deviceClass,
      starter.markerColor,
      starter.startTime,
      starter.endTime,
      formatTimeDuration(starter.manualFinishDuration),
      starter.key,
      starter.id,
      starter.deviceId,
      starter.deviceName,
    ];

    if (possibleTags.length > 0) {
      row.push(...formatNestedObject(starter.tags, possibleTags));
    }
    if (possibleTimes.length > 0) {
      row.push(...formatNestedObject(starter.times, possibleTimes));
    }

    for (const [, link] of links) row.push(link(starter));

    row = row.map((i) => (i == null ? '' : i));
    if (row.some((i) => i !== '')) {
      csvString += `\r\n${row.join(';').replace(/(\r\n|\n|\r)/gm, '')}`;
    }
  }

  return csvString;
}

export function validateRawStarters(starters: Array<Partial<RacemapStarter>>): void {
  for (const starter of starters) {
    if (
      starter.deviceClass != null &&
      !Object.values(DeviceClasses).includes(starter.deviceClass)
    ) {
      throw new Error(
        `Participant ${
          starter.name || starter.appId || starter.id
        } has invalid device class. Possible devices classes are ${Object.values(
          DeviceClasses,
        ).join(',')}.`,
      );
    }

    if (starter.appId != null && !validateStarterAppId(starter.appId)) {
      throw new Error(
        `Participant ${
          starter.name || starter.appId || starter.id
        } has invalid app id. The app id has to be a valid IMEI, UUID or transponder id.`,
      );
    }
  }
}
